import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { useRolodexEdit } from 'features/fde'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { AnnotationScreenshots } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/AnnotationScreenshots'
import { DamageTypeSelector } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/DamageTypeSelector'
import { Form781ASelector } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/Form781ASelector'
import { G1N6DimensionsSection } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/G1N6DimensionsSection'
import { LocationSection } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/LocationSection'
import { PhotoSelector } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/PhotoSelector'
import { SeverityPicker } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/SeverityPicker'
import { TagSelector } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/TagSelector'
import { useAnnotationForms } from 'features/kestrelViewer/ui/popupMenu/annotationMenu/useAnnotationForms'
import { ConfirmationModal } from 'features/kestrelViewer/ui/popupMenu/shared/ConfirmationModal'
import { MarkupLabelInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupLabelInput'
import { MarkupNotesInput } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupNotesInput'
import {
  Button,
  ButtonRow,
  CenteredContent,
  DeleteButton,
  FooterSection,
  InputGroup,
  InputHeader,
  MarkupPopupContainer,
  SectionHeader,
} from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useEscapeKey } from 'features/kestrelViewer/ui/popupMenu/shared/useEscapeKey'
import {
  formatArea,
  formatLengthFromUnrealUnits,
  formatPerimeter,
} from 'features/kestrelViewer/utils/unitConversionUtils'
import { useGeckoLocale } from 'features/locale'
import { generateEntrySlug } from 'features/rolodex'
import { Eye, EyeOff, MapPin } from 'lucide-react'
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import {
  KestrelInspectionType,
  KVAnnotation,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'

import { getSeverityConfiguration } from './severityConfigurations'
import { useAnnotationChangeManager } from './useAnnotationChangeManager'
import { useAnnotationGeospatialLink } from './useAnnotationGeospatialLink'

const SeveritySavePhotoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
`

const SeveritySection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const MeasurementsRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${({ $columns }) =>
    $columns === 4 ? 'auto auto auto auto' : 'auto auto'};
  gap: 0.5rem ${({ $columns }) => ($columns === 4 ? '1rem' : '0.75rem')};
  width: 100%;
  margin-top: 0.5rem;
  justify-content: start;
  align-items: start;
`

const MeasurementCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`

const MeasurementLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 18px;
  min-width: 0;
`

const AreaPerimeterValue = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`

const AreaPerimeterLabel = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`

const AreaPerimeterSection = styled.div<{ $isAerospace: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: ${({ $isAerospace }) => ($isAerospace ? 'column' : 'row')};
  justify-content: flex-end;
  align-items: ${({ $isAerospace }) =>
    $isAerospace ? 'flex-start' : 'center'};
  gap: 1rem;
`

const TopRowRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
`

const LocationIconButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(59, 130, 246, 0.9);
  text-decoration: none;
  padding: 0.4rem;
  border-radius: 4px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(59, 130, 246, 0.2);
    border-color: rgba(59, 130, 246, 0.5);
    color: rgba(96, 165, 250, 1);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SplitInputRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  align-items: center;

  & > * {
    flex: 1;
  }
`

const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.25rem 0;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

const ChevronIcon = styled(ChevronDownIcon)<{ $isExpanded: boolean }>`
  width: 1rem;
  height: 1rem;
  transition: transform 0.2s ease;
  transform: ${(props) =>
    props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

const CollapsibleContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${(props) => (props.$isExpanded ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`

const pointAnnotationFields = [
  'annotationLabel',
  'annotationSeverity',
  'aerospaceAnnotation',
  'rooftopAnnotation',
]
const polygonAnnotationFields = [
  'surfaceAreaLabel',
  'surfaceAreaSeverity',
  'aerospaceAnnotation',
  'rooftopAnnotation',
]

const BoundingBoxVisibilityButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.125rem;
  margin-left: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  line-height: 0;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 14px;
    height: 14px;
    display: block;
  }
`

export const AnnotationMenu: FC<{
  selectedAnnotation: KVAnnotation
}> = ({ selectedAnnotation }) => {
  const { setSelectedMarkup, popupMenuStartPosition, currentUnit } =
    useKestrelViewerContext()
  const {
    kestrelModel,
    sendKVCommand,
    annotations,
    setAnnotations,
    gatekeeper,
    updateAnnotation,
  } = useKestrel()
  const { rolodexPatch } = useRolodexEdit()
  const [isReportContentExpanded, setIsReportContentExpanded] = useState(false)
  const [is781AFormsExpanded, setIs781AFormsExpanded] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const [showTagControls, setShowTagControls] = useState(false)
  const [selectedUnitId, setSelectedUnitId] = useState<string>('')
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false)

  const [editedAnnotation, setEditedAnnotation] = useState<KVAnnotation>(() => {
    return JSON.parse(JSON.stringify(selectedAnnotation))
  })

  // Get live annotation from shared state to stay in sync with other editors
  const liveAnnotation = useMemo(
    () => annotations?.find((a) => a.id === selectedAnnotation.id),
    [annotations, selectedAnnotation.id]
  )

  const { unitOptions, siteUnits, kestrelModelUnits, siteId, orgId } =
    useKestrel()

  const labelInputRef = useRef<HTMLInputElement>(null)
  const notesInputRef = useRef<HTMLTextAreaElement>(null)

  // Get severity configuration based on gatekeeper kind
  const severityConfig = getSeverityConfiguration(gatekeeper.kind)

  const form781A = useAnnotationForms({
    kestrelModelId: kestrelModel?.id,
    annotation: editedAnnotation,
  })

  const quickSave = useCallback(() => {
    if (!editedAnnotation.label || editedAnnotation.label.trim() === '') {
      return
    }
    // Determine severity via configuration
    const severityValue = getSeverityConfiguration(
      gatekeeper.kind
    ).getCurrentSeverity(editedAnnotation)

    updateAnnotation(
      selectedAnnotation,
      selectedAnnotation.geometry?.kind === 'point'
        ? pointAnnotationFields
        : polygonAnnotationFields,
      (a) => {
        a.label = editedAnnotation.label
        a.notes = editedAnnotation.notes
        a.severity = editedAnnotation.severity
        a.navyAnnotation = editedAnnotation.navyAnnotation
        a.rooftopAnnotation = editedAnnotation.rooftopAnnotation
        a.g1n6Annotation = editedAnnotation.g1n6Annotation
        a.aerospaceAnnotation = editedAnnotation.aerospaceAnnotation
        a.imageNames = editedAnnotation.imageNames
        a.tags = editedAnnotation.tags
        return a
      },
      [
        editedAnnotation.label,
        severityValue,
        editedAnnotation.rooftopAnnotation,
        editedAnnotation.aerospaceAnnotation,
      ]
    )
  }, [updateAnnotation, selectedAnnotation, editedAnnotation, gatekeeper.kind])

  useEffect(() => {
    setEditedAnnotation(JSON.parse(JSON.stringify(selectedAnnotation)))
  }, [selectedAnnotation])

  // Stringify for stable dependency comparison
  const liveDamageTypesJson = JSON.stringify(
    liveAnnotation?.rooftopAnnotation?.selected
  )

  // Sync with live annotation from shared state to stay in sync with DataValidationPanel
  useEffect(() => {
    if (!liveAnnotation) return

    setEditedAnnotation((prev) => {
      // Only update fields that may have been changed externally
      // to avoid overwriting the user's in-progress edits on other fields
      const labelChanged = prev.label !== liveAnnotation.label
      const notesChanged = prev.notes !== liveAnnotation.notes
      const damageTypesChanged =
        JSON.stringify(prev.rooftopAnnotation?.selected) !==
        JSON.stringify(liveAnnotation.rooftopAnnotation?.selected)
      const severityChanged = prev.severity !== liveAnnotation.severity

      if (
        !labelChanged &&
        !notesChanged &&
        !damageTypesChanged &&
        !severityChanged
      ) {
        return prev
      }

      return {
        ...prev,
        label: liveAnnotation.label,
        notes: liveAnnotation.notes,
        severity: liveAnnotation.severity,
        rooftopAnnotation: liveAnnotation.rooftopAnnotation
          ? { ...liveAnnotation.rooftopAnnotation }
          : prev.rooftopAnnotation,
      }
    })
  }, [
    liveAnnotation?.label,
    liveAnnotation?.notes,
    liveAnnotation?.severity,
    liveAnnotation,
    liveDamageTypesJson,
  ])

  // Unified change detection and auto-save manager
  useAnnotationChangeManager(editedAnnotation, gatekeeper.kind, quickSave)

  useEscapeKey(() => setSelectedMarkup(undefined))

  // Focus on label input when popup opens
  useEffect(() => {
    if (labelInputRef.current) {
      labelInputRef.current.focus()
    }
  }, [])

  const handleTabKey = (
    e: React.KeyboardEvent,
    targetRef: React.RefObject<HTMLElement | null>
  ) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (targetRef.current) {
        targetRef.current.focus()
      }
    }
  }

  const onClose = () => {
    setSelectedMarkup(undefined)
  }

  const onDelete = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    let filteredAnnotations: KVAnnotation[] = []
    if (annotations) {
      filteredAnnotations = annotations.filter(
        (a) => a.id !== selectedAnnotation.id
      )
    }
    if (sendKVCommand) {
      setAnnotations(filteredAnnotations)
      sendDeleteKvCommandForAnnotation()
      setSelectedMarkup(undefined)
      const patchData: RolodexEntryPatchJson = {
        id: kestrelModel?.id ?? '',
        data: {
          annotations:
            filteredAnnotations?.map((a) => KVAnnotation.toJSON(a)) ?? [],
        },
      }
      rolodexPatch([patchData])
    }
    setShowDeleteConfirm(false)
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }

  const { locale } = useGeckoLocale()
  const disableEditing = editedAnnotation.historical

  const annotationLabel = selectedAnnotation.label || 'this annotation'

  const sendDeleteKvCommandForAnnotation = React.useCallback(() => {
    if (!sendKVCommand) return
    if (selectedAnnotation.geometry?.kind === 'point') {
      sendKVCommand({
        command: KestrelCommands.DELETE_ANNOTATION,
        annotationId: selectedAnnotation.id,
      })
    } else if (selectedAnnotation.geometry?.kind === 'polygon') {
      sendKVCommand({
        command: KestrelCommands.DELETE_SURFACE_AREA,
        surfaceAreaId: selectedAnnotation.id,
      })
    }
  }, [
    sendKVCommand,
    selectedAnnotation?.geometry?.kind,
    selectedAnnotation?.id,
  ])

  const applyTag = async () => {
    if (!kestrelModel || !selectedUnitId) return

    const existingLink = kestrelModelUnits.find(
      (l) => l.tags[RolodexEntryType.UNIT] === selectedUnitId
    )

    const replaceNeeded = !!existingLink?.data?.annotation

    if (existingLink && replaceNeeded) {
      setShowReplaceConfirm(true)
      return
    }

    await doTag(existingLink?.id)
  }

  const doTag = async (existingLinkId?: string) => {
    if (!kestrelModel || !selectedUnitId) return

    const selectedUnit = siteUnits.find((u) => u.id === selectedUnitId)
    const assetName = selectedUnit?.data.name || selectedAnnotation.label
    const annotationJson = {
      ...KVAnnotation.toJSON(selectedAnnotation),
      annotationLabel: assetName,
    }
    const effectiveOrgId = orgId || selectedUnit?.tags.organization
    const effectiveSiteId = siteId || selectedUnit?.tags.site

    try {
      const patch: RolodexEntryPatchJson = existingLinkId
        ? {
            id: existingLinkId,
            data: { annotation: annotationJson },
          }
        : {
            id: generateEntrySlug('unit-' + selectedUnitId),
            type: RolodexEntryType.KESTREL_MODEL_UNIT,
            data: { annotation: annotationJson },
            tags: {
              ...(effectiveOrgId ? { organization: effectiveOrgId } : {}),
              ...(effectiveSiteId ? { site: effectiveSiteId } : {}),
              [RolodexEntryType.KESTREL_MODEL]: kestrelModel.id,
              [RolodexEntryType.UNIT]: selectedUnitId,
            },
          }
      await rolodexPatch([patch])

      let filteredAnnotations: KVAnnotation[] = []
      if (annotations) {
        filteredAnnotations = annotations.filter(
          (a) => a.id !== selectedAnnotation.id
        )
      }
      if (sendKVCommand) {
        setAnnotations(filteredAnnotations)
        sendDeleteKvCommandForAnnotation()
        sendDeleteKvCommandForAnnotation()
        const patchData: RolodexEntryPatchJson = {
          id: kestrelModel?.id ?? '',
          data: {
            annotations:
              filteredAnnotations?.map((a) => KVAnnotation.toJSON(a)) ?? [],
          },
        }
        await rolodexPatch([patchData])
      }

      setSelectedMarkup(undefined)
      setShowTagControls(false)
      setSelectedUnitId('')
    } catch (e) {
      console.error('Failed to tag unit:', e)
    }
  }

  const geospatialLink = useAnnotationGeospatialLink(
    kestrelModel,
    editedAnnotation,
    gatekeeper.annotationForm.showGeospatialLink
  )

  // Get measurement scale from Kestrel model
  const measurementScale = kestrelModel?.data.measurementScale ?? 100

  const [boundingBoxVisible, setBoundingBoxVisible] = useState(false)

  // Reset bounding box visibility when annotation changes (default to hidden)
  useEffect(() => {
    setBoundingBoxVisible(false)
  }, [selectedAnnotation.id])

  return (
    <>
      <MarkupPopupContainer
        popupMenuStartPosition={popupMenuStartPosition}
        setSelectedMarkup={setSelectedMarkup}
        width='28rem'>
        {gatekeeper.annotationForm.showLabelInput && (
          <SplitInputRow>
            <InputGroup>
              <InputHeader>Label</InputHeader>
              <MarkupLabelInput
                ref={labelInputRef}
                value={editedAnnotation.label}
                onChange={(value) =>
                  setEditedAnnotation((prev) => ({ ...prev, label: value }))
                }
                placeholder='Enter label...'
                disabled={editedAnnotation.historical}
                onEnter={quickSave}
                onTabKey={(e) => handleTabKey(e, notesInputRef)}
                requireNonEmpty={true}
                warningText='Label is required.'
              />
            </InputGroup>
          </SplitInputRow>
        )}

        {/* Severity and Area/Perimeter Row */}
        {gatekeeper.annotationForm.showTopSeverityRow && (
          <SeveritySavePhotoRow>
            <SeveritySection>
              <InputHeader>{severityConfig.headerText}</InputHeader>
              <SeverityPicker
                possibleValues={severityConfig.possibleValues}
                currentSeverity={severityConfig.getCurrentSeverity(
                  editedAnnotation
                )}
                setSeverity={(s: string) =>
                  severityConfig.setSeverity(
                    editedAnnotation,
                    s,
                    setEditedAnnotation
                  )
                }
                disabled={disableEditing}
              />
            </SeveritySection>

            <TopRowRight>
              {/* For aerospace: show measurements in separate row below, location button here */}
              {/* For non-aerospace: show measurements here on the right */}
              {editedAnnotation.geometry?.kind === 'polygon' &&
              editedAnnotation.area !== undefined &&
              editedAnnotation.perimeter !== undefined &&
              !(
                gatekeeper.kind === KestrelInspectionType.AEROSPACE ||
                editedAnnotation.aerospaceAnnotation
              ) ? (
                <AreaPerimeterSection $isAerospace={false}>
                  <div>
                    <AreaPerimeterLabel>Area</AreaPerimeterLabel>
                    <AreaPerimeterValue>
                      {formatArea(editedAnnotation.area, currentUnit, locale)}
                    </AreaPerimeterValue>
                  </div>
                  <div>
                    <AreaPerimeterLabel>Perimeter</AreaPerimeterLabel>
                    <AreaPerimeterValue>
                      {formatPerimeter(
                        editedAnnotation.perimeter,
                        currentUnit,
                        locale
                      )}
                    </AreaPerimeterValue>
                  </div>
                </AreaPerimeterSection>
              ) : (
                // Google Maps Location Button (for aerospace or when no measurements)
                gatekeeper.annotationForm.showGeospatialLink &&
                geospatialLink && (
                  <LocationIconButton
                    href={geospatialLink}
                    target='_blank'
                    rel='noopener noreferrer'
                    title='View location on Google Maps'
                    onClick={(e) => e.stopPropagation()}>
                    <MapPin />
                  </LocationIconButton>
                )
              )}
            </TopRowRight>
          </SeveritySavePhotoRow>
        )}

        {/* Measurements row under label + severity - ONLY for aerospace */}
        {editedAnnotation.geometry?.kind === 'polygon' &&
          editedAnnotation.area !== undefined &&
          editedAnnotation.perimeter !== undefined &&
          (gatekeeper.kind === KestrelInspectionType.AEROSPACE ||
            editedAnnotation.aerospaceAnnotation) && (
            <MeasurementsRow
              $columns={
                editedAnnotation.isSegmentationSurfaceArea &&
                editedAnnotation.boundingBoxLength !== undefined &&
                editedAnnotation.boundingBoxWidth !== undefined &&
                (gatekeeper.kind === KestrelInspectionType.AEROSPACE ||
                  editedAnnotation.aerospaceAnnotation)
                  ? 4
                  : 2
              }>
              <MeasurementCell>
                <MeasurementLabelRow>
                  <AreaPerimeterLabel>Area</AreaPerimeterLabel>
                </MeasurementLabelRow>
                <AreaPerimeterValue>
                  {formatArea(editedAnnotation.area, currentUnit, locale)}
                </AreaPerimeterValue>
              </MeasurementCell>

              <MeasurementCell>
                <MeasurementLabelRow>
                  <AreaPerimeterLabel>Perimeter</AreaPerimeterLabel>
                </MeasurementLabelRow>
                <AreaPerimeterValue>
                  {formatPerimeter(
                    editedAnnotation.perimeter,
                    currentUnit,
                    locale
                  )}
                </AreaPerimeterValue>
              </MeasurementCell>

              {/* Only show bounding box for aerospace segmentation surface areas */}
              {editedAnnotation.isSegmentationSurfaceArea &&
                editedAnnotation.boundingBoxLength !== undefined &&
                editedAnnotation.boundingBoxWidth !== undefined &&
                (gatekeeper.kind === KestrelInspectionType.AEROSPACE ||
                  editedAnnotation.aerospaceAnnotation) && (
                  <>
                    {/* Bounding Box Dimensions */}
                    <MeasurementCell>
                      <MeasurementLabelRow>
                        <AreaPerimeterLabel>Bounding Dims</AreaPerimeterLabel>
                      </MeasurementLabelRow>
                      <AreaPerimeterValue>
                        {formatLengthFromUnrealUnits(
                          editedAnnotation.boundingBoxLength,
                          measurementScale,
                          currentUnit,
                          locale
                        )}{' '}
                        ×{' '}
                        {formatLengthFromUnrealUnits(
                          editedAnnotation.boundingBoxWidth,
                          measurementScale,
                          currentUnit,
                          locale
                        )}
                      </AreaPerimeterValue>
                    </MeasurementCell>

                    {/* Bounding Box Area */}
                    <MeasurementCell>
                      <MeasurementLabelRow>
                        <AreaPerimeterLabel>Bounding Area</AreaPerimeterLabel>
                        <BoundingBoxVisibilityButton
                          onClick={(e) => {
                            e.stopPropagation()
                            const newVisibility = !boundingBoxVisible
                            setBoundingBoxVisible(newVisibility)
                            if (sendKVCommand) {
                              sendKVCommand({
                                command: newVisibility
                                  ? KestrelCommands.SHOW_SEGMENTATION_BBOX
                                  : KestrelCommands.HIDE_SEGMENTATION_BBOX,
                                annotationId: editedAnnotation.id,
                              })
                            }
                          }}
                          title={
                            boundingBoxVisible
                              ? 'Hide bounding box'
                              : 'Show bounding box'
                          }>
                          {boundingBoxVisible ? (
                            <Eye size={14} />
                          ) : (
                            <EyeOff size={14} />
                          )}
                        </BoundingBoxVisibilityButton>
                      </MeasurementLabelRow>
                      <AreaPerimeterValue>
                        {(() => {
                          if (
                            editedAnnotation.boundingBoxLength === undefined ||
                            editedAnnotation.boundingBoxWidth === undefined
                          ) {
                            return 'N/A'
                          }
                          // Convert Unreal units to cm, then calculate area in cm²
                          const lengthInCm =
                            editedAnnotation.boundingBoxLength *
                            measurementScale
                          const widthInCm =
                            editedAnnotation.boundingBoxWidth * measurementScale
                          const areaInCm2 = lengthInCm * widthInCm
                          return formatArea(areaInCm2, currentUnit, locale)
                        })()}
                      </AreaPerimeterValue>
                    </MeasurementCell>
                  </>
                )}
            </MeasurementsRow>
          )}

        {/* Damage Type/Category */}
        {gatekeeper.annotationForm.showDamageTypeSelector && (
          <InputGroup>
            <InputHeader>{gatekeeper.annotationForm.damageLabel}</InputHeader>
            <CenteredContent>
              <DamageTypeSelector
                editedAnnotation={editedAnnotation}
                setEditedAnnotation={setEditedAnnotation}
                inspectionType={gatekeeper.kind}
              />
            </CenteredContent>
          </InputGroup>
        )}

        {/* Severity under Damage section */}
        {gatekeeper.annotationForm.severityBelowDamage && (
          <InputGroup>
            <InputHeader>{severityConfig.headerText}</InputHeader>
            <SeverityPicker
              possibleValues={severityConfig.possibleValues}
              currentSeverity={severityConfig.getCurrentSeverity(
                editedAnnotation
              )}
              setSeverity={(s: string) =>
                severityConfig.setSeverity(
                  editedAnnotation,
                  s,
                  setEditedAnnotation
                )
              }
              disabled={disableEditing}
            />
          </InputGroup>
        )}

        {/* G1N6 Dimensions */}
        {gatekeeper.annotationForm.showG1N6Dimensions && (
          <InputGroup>
            <InputHeader>Dimensions</InputHeader>
            <G1N6DimensionsSection
              annotation={editedAnnotation}
              currentUnit={currentUnit}
              setAnnotation={setEditedAnnotation}
            />
          </InputGroup>
        )}

        {/* G1N6 Location */}
        {gatekeeper.annotationForm.showG1N6Location && (
          <InputGroup>
            <InputHeader>Location</InputHeader>
            <LocationSection
              locationTransverse={
                editedAnnotation.g1n6Annotation?.locationTransverse || ''
              }
              locationLongitudinal={
                editedAnnotation.g1n6Annotation?.locationLongitudinal || ''
              }
              locationVertical={
                editedAnnotation.g1n6Annotation?.locationVertical || ''
              }
              onLocationChange={(field, value) => {
                if (!editedAnnotation.g1n6Annotation) return
                setEditedAnnotation({
                  ...editedAnnotation,
                  g1n6Annotation: {
                    ...editedAnnotation.g1n6Annotation,
                    [field]: value,
                  },
                })
              }}
              disabled={editedAnnotation.historical}
            />
          </InputGroup>
        )}

        {/* Navy-specific location section */}
        {gatekeeper.annotationForm.showNavyLocation && (
          <InputGroup>
            <InputHeader>Location</InputHeader>
            <LocationSection
              locationTransverse={
                editedAnnotation.navyAnnotation?.locationTransverse || ''
              }
              locationLongitudinal={
                editedAnnotation.navyAnnotation?.locationLongitudinal || ''
              }
              locationVertical={
                editedAnnotation.navyAnnotation?.locationVertical || ''
              }
              onLocationChange={(field, value) => {
                if (!editedAnnotation.navyAnnotation) return
                setEditedAnnotation({
                  ...editedAnnotation,
                  navyAnnotation: {
                    ...editedAnnotation.navyAnnotation,
                    [field]: value,
                  },
                })
              }}
              disabled={editedAnnotation.historical}
            />
          </InputGroup>
        )}

        {/* Tags */}
        {gatekeeper.annotationForm.showTags && (
          <InputGroup>
            <InputHeader>Tags</InputHeader>
            <TagSelector
              editedAnnotation={editedAnnotation}
              setEditedAnnotation={setEditedAnnotation}
            />
          </InputGroup>
        )}

        {/* 781A Forms */}
        {gatekeeper.kind === KestrelInspectionType.AEROSPACE &&
          form781A.inProgressForms.length > 0 && (
            <InputGroup>
              <CollapsibleHeader
                onClick={() => setIs781AFormsExpanded(!is781AFormsExpanded)}>
                <SectionHeader>
                  781A Forms ({form781A.inProgressForms.length})
                </SectionHeader>
                <ChevronIcon $isExpanded={is781AFormsExpanded} />
              </CollapsibleHeader>
              <CollapsibleContent $isExpanded={is781AFormsExpanded}>
                <InputGroup>
                  <Form781ASelector
                    inProgressForms={form781A.inProgressForms}
                    isFormChecked={form781A.isFormChecked}
                    toggleFormSelection={form781A.toggleFormSelection}
                    disabled={editedAnnotation.historical}
                  />
                </InputGroup>
              </CollapsibleContent>
            </InputGroup>
          )}

        {/* Screenshots */}
        {gatekeeper.annotationForm.showScreenshots && (
          <InputGroup>
            <CollapsibleHeader
              onClick={() =>
                setIsReportContentExpanded(!isReportContentExpanded)
              }>
              <SectionHeader>Screenshots</SectionHeader>
              <ChevronIcon $isExpanded={isReportContentExpanded} />
            </CollapsibleHeader>
            <CollapsibleContent $isExpanded={isReportContentExpanded}>
              <InputGroup>
                <AnnotationScreenshots
                  editedAnnotation={editedAnnotation}
                  setEditedAnnotation={setEditedAnnotation}
                />
              </InputGroup>
            </CollapsibleContent>
          </InputGroup>
        )}

        {/* Photo Selector Row */}
        {kestrelModel && kestrelModel.data.photosEnabled && (
          <InputGroup>
            <InputHeader>
              Photos{' '}
              <span style={{ fontSize: '0.65rem', opacity: 0.75 }}>
                ({editedAnnotation.imageNames?.length || 0} photos selected)
              </span>
            </InputHeader>
            <PhotoSelector
              editedAnnotation={editedAnnotation}
              setEditedAnnotation={setEditedAnnotation}
            />
          </InputGroup>
        )}

        {/* Notes */}
        {gatekeeper.annotationForm.showNotes && (
          <InputGroup>
            <InputHeader>Notes</InputHeader>
            <MarkupNotesInput
              ref={notesInputRef}
              value={editedAnnotation.notes}
              onChange={(value) =>
                setEditedAnnotation((prev) => ({ ...prev, notes: value }))
              }
              placeholder='Add notes...'
              disabled={disableEditing}
              onEnter={quickSave}
              onTabKey={(e) => handleTabKey(e, labelInputRef)}
            />
          </InputGroup>
        )}

        {/* Tag Unit and Close/Delete Buttons Row */}
        <FooterSection>
          {gatekeeper.feature.unitTaggingControls && showTagControls && (
            <InputGroup>
              <InputHeader>Select Unit</InputHeader>
              <div onMouseDown={(e) => e.stopPropagation()}>
                <Dropdown
                  options={unitOptions}
                  onValueChange={(v) => setSelectedUnitId(v)}
                  placeholder='Choose a unit...'
                />
              </div>
            </InputGroup>
          )}
          {gatekeeper.feature.unitTaggingControls && (
            <ButtonRow>
              <Button
                onClick={() => setShowTagControls((s) => !s)}
                style={{ marginBottom: '0.5rem' }}>
                {showTagControls ? 'Hide' : 'Tag Unit'}
              </Button>
              {showTagControls && (
                <Button disabled={!selectedUnitId} onClick={() => applyTag()}>
                  Apply
                </Button>
              )}
            </ButtonRow>
          )}

          <InputGroup>
            <CenteredContent>
              <ButtonRow>
                {disableEditing ? (
                  <Button onClick={onClose}>Close</Button>
                ) : (
                  <>
                    <Button onClick={onClose}>Close</Button>
                    <DeleteButton onClick={onDelete}>Delete</DeleteButton>
                  </>
                )}
              </ButtonRow>
            </CenteredContent>
          </InputGroup>
        </FooterSection>
      </MarkupPopupContainer>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title='Delete Annotation'
        message={`Are you sure you want to delete "${annotationLabel}"? This action cannot be undone.`}
        confirmText='Delete'
        cancelText='Cancel'
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {gatekeeper.feature.unitTaggingControls && (
        <ConfirmationModal
          isOpen={showReplaceConfirm}
          title='Replace existing unit tag?'
          message={
            'This unit already has a tagged annotation.\nDo you want to replace it?'
          }
          confirmText='Replace'
          cancelText='Cancel'
          onConfirm={() => {
            setShowReplaceConfirm(false)
            doTag(
              kestrelModelUnits.find(
                (l) => l.tags[RolodexEntryType.UNIT] === selectedUnitId
              )?.id
            )
          }}
          onCancel={() => setShowReplaceConfirm(false)}
        />
      )}
    </>
  )
}
